import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getYouTubeAuthUrl, getTwitchAuthUrl, getKickAuthUrl, getConnectionStatus, disconnectProvider } from '../api/oauth';
import { useNotifications } from './NotificationContext';

const AccountConnections = ({ channelCounts = {}, onOpenChannels }) => {
    const [connections, setConnections] = useState({
        youtube: false,
        twitch: false,
        kick: false,
        youtubeNeedsReauth: false,
        twitchNeedsReauth: false,
        kickNeedsReauth: false,
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [connecting, setConnecting] = useState(null); // 'youtube' or 'twitch'
    const [disconnecting, setDisconnecting] = useState(null);
    const [pendingDisconnect, setPendingDisconnect] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const [searchParams, setSearchParams] = useSearchParams();
    const { refreshPlatformChannels, refreshSubscriptions } = useNotifications();

    useEffect(() => {
        fetchConnections();
        
        // Check for OAuth success/error in URL params
        const oauthStatus = searchParams.get('oauth');
        const provider = searchParams.get('provider');
        const errorParam = searchParams.get('error');
        
        if (oauthStatus === 'success' && provider) {
            // OAuth succeeded - refresh connections and show success
            const labels = { youtube: 'YouTube', twitch: 'Twitch', kick: 'Kick' };
            setSuccessMessage(`${labels[provider] || provider} account connected successfully!`);
            fetchConnections();
            // A new connection syncs followed channels: refresh notification data
            // so the Notifications tab reflects them without a reload
            refreshPlatformChannels();
            refreshSubscriptions();
            // Clear the URL params after a moment
            setTimeout(() => {
                setSearchParams({ tab: 'account' }, { replace: true });
                setSuccessMessage(null);
            }, 3000);
        } else if (oauthStatus === 'error' && errorParam) {
            // OAuth failed - show error
            setError(decodeURIComponent(errorParam));
            // Clear the URL params
            setSearchParams({ tab: 'account' }, { replace: true });
        }
    }, []);

    const fetchConnections = async () => {
        try {
            setLoading(true);
            setError(null);
            const { youtube, twitch, kick, youtubeNeedsReauth, twitchNeedsReauth, kickNeedsReauth } = await getConnectionStatus();
            setConnections({
                youtube,
                twitch,
                kick,
                youtubeNeedsReauth,
                twitchNeedsReauth,
                kickNeedsReauth,
            });
        } catch (err) {
            console.error('Error fetching connections:', err);
            setError('Failed to load account connections. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleConnect = async (provider) => {
        try {
            setConnecting(provider);
            setError(null);
            
            let authUrl;
            if (provider === 'youtube') {
                authUrl = await getYouTubeAuthUrl();
            } else if (provider === 'twitch') {
                authUrl = await getTwitchAuthUrl();
            } else if (provider === 'kick') {
                authUrl = await getKickAuthUrl();
            } else {
                throw new Error('Invalid provider');
            }

            // Validate URL before redirecting
            if (!authUrl || !authUrl.startsWith('http')) {
                throw new Error(`Invalid ${provider} authorization URL received from backend`);
            }

            // Redirect user to OAuth consent screen
            window.location.href = authUrl;
        } catch (err) {
            console.error(`Error connecting ${provider}:`, err);
            
            // Provide more detailed error messages
            let errorMessage = err.message || `Failed to connect ${provider}. Please try again.`;
            
            // Check for specific backend configuration errors
            if (err.message?.includes('client_id') || err.message?.includes('client ID') || err.message?.includes('client_id is not configured')) {
                // More user-friendly message for configuration errors
                errorMessage = `OAuth service is not properly configured. The backend is missing required credentials. Please contact support or try again later.`;
            } else if (err.message?.includes('Backend configuration error')) {
                // Keep the technical message but make it clearer
                errorMessage = err.message;
            } else if (err.message?.includes('Backend') || err.message?.includes('backend')) {
                errorMessage = err.message;
            } else if (err.response?.status === 500) {
                errorMessage = `Backend server error. The ${provider} OAuth service may not be properly configured. Please try again later or contact support.`;
            } else if (err.response?.status === 404) {
                errorMessage = `OAuth endpoint not found. Please ensure the backend is running and the ${provider} OAuth routes are configured.`;
            }
            
            setError(errorMessage);
            setConnecting(null);
        }
    };

    const handleDisconnect = async (provider) => {
        try {
            setDisconnecting(provider);
            setError(null);
            await disconnectProvider(provider);
            // Refresh connections and synced channel data after disconnect
            await fetchConnections();
            refreshPlatformChannels();
        } catch (err) {
            console.error(`Error disconnecting ${provider}:`, err);
            setError(`Failed to disconnect ${provider}. Please try again.`);
        } finally {
            setDisconnecting(null);
            setPendingDisconnect(null);
        }
    };

    const YouTubeLogo = () => (
        <svg width="30" height="30" viewBox="0 0 24 24" fill="#fff" aria-hidden="true">
            <path d="M23.498 6.186a2.994 2.994 0 0 0-2.108-2.116C19.228 3.5 12 3.5 12 3.5s-7.228 0-9.39.57A2.994 2.994 0 0 0 .502 6.186C0 8.36 0 12 0 12s0 3.64.502 5.814a2.994 2.994 0 0 0 2.108 2.116C4.772 20.5 12 20.5 12 20.5s7.228 0 9.39-.57a2.994 2.994 0 0 0 2.108-2.116C24 15.64 24 12 24 12s0-3.64-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
        </svg>
    );

    const TwitchLogo = () => (
        <svg width="26" height="26" viewBox="0 0 24 24" fill="#fff" aria-hidden="true">
            <path d="M2.857 0L.286 4.286V21.43h5.143V24h3.429l3.429-2.571h4.286L23.714 16V0H2.857zm17.143 15.429l-2.571 2.571h-4.286l-3.429 2.571v-2.571H3.429V1.714h16.571v13.715z" />
        </svg>
    );

    const KickLogo = () => (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="#111" aria-hidden="true">
            <path d="M2 2h7.2v20H2V2zm8.4 8.4L16.8 2H22l-7.2 9.6L22 22h-5.2l-6.4-8.4V22H10.4V2h6.4v8.4H10.4z" />
        </svg>
    );

    if (loading) {
        return (
            <p className="profile-empty">Loading account connections...</p>
        );
    }

    return (
        <div className="account-connections">
            {successMessage && (
                <p className="profile-success">{successMessage}</p>
            )}

            {error && (
                <p className="profile-error">{error}</p>
            )}

            {[
                { key: 'youtube', label: 'YouTube', gradient: 'linear-gradient(135deg, #FF0000 0%, #CC0000 100%)', Logo: YouTubeLogo },
                { key: 'twitch', label: 'Twitch', gradient: 'linear-gradient(135deg, #9146FF 0%, #772CE8 100%)', Logo: TwitchLogo },
                { key: 'kick', label: 'Kick', gradient: 'linear-gradient(135deg, #53FC18 0%, #2DB80E 100%)', Logo: KickLogo },
            ].map(({ key, label, gradient, Logo }) => {
                const isConnected = connections[key];
                const needsReauth = connections[`${key}NeedsReauth`];
                const isConnecting = connecting === key;
                const isDisconnecting = disconnecting === key;
                const isPendingDisconnect = pendingDisconnect === key;
                const channelCount = channelCounts[key] || 0;

                return (
                    <div key={key} className="account-connect-card">
                        <div className="account-connect-brand">
                            <div className="account-connect-logo" style={{ background: gradient }}>
                                <Logo />
                            </div>
                            <div>
                                <div className="profile-settings-title">{label}</div>
                                <div className="profile-settings-hint">
                                    {isConnected
                                        ? (needsReauth
                                            ? 'Connection expired — reconnect to keep live alerts'
                                            : (
                                                <>
                                                    Connected
                                                    {channelCount > 0 && onOpenChannels ? (
                                                        <>
                                                            {' · '}
                                                            <button
                                                                type="button"
                                                                className="profile-settings-hint-btn"
                                                                onClick={() => onOpenChannels(key)}
                                                            >
                                                                {channelCount} channel{channelCount === 1 ? '' : 's'} synced
                                                            </button>
                                                        </>
                                                    ) : null}
                                                </>
                                            ))
                                        : 'Not connected'}
                                </div>
                            </div>
                        </div>
                        <div className="account-connect-actions">
                            {isConnected ? (
                                isPendingDisconnect ? (
                                    <>
                                        <span className="profile-settings-hint">Disconnect {label}?</span>
                                        <button
                                            type="button"
                                            className="profile-btn profile-btn--danger"
                                            onClick={() => handleDisconnect(key)}
                                            disabled={isDisconnecting}
                                        >
                                            {isDisconnecting ? 'Disconnecting...' : 'Yes, disconnect'}
                                        </button>
                                        <button
                                            type="button"
                                            className="profile-btn"
                                            onClick={() => setPendingDisconnect(null)}
                                            disabled={isDisconnecting}
                                        >
                                            Cancel
                                        </button>
                                    </>
                                ) : (
                                    <>
                                        {needsReauth ? (
                                            <button
                                                type="button"
                                                className="profile-btn"
                                                onClick={() => handleConnect(key)}
                                                disabled={isConnecting}
                                            >
                                                {isConnecting ? 'Reconnecting...' : 'Reconnect'}
                                            </button>
                                        ) : (
                                            <span className="profile-status-pill is-on">On</span>
                                        )}
                                        <button
                                            type="button"
                                            className="profile-btn profile-btn--danger"
                                            onClick={() => setPendingDisconnect(key)}
                                        >
                                            Disconnect
                                        </button>
                                    </>
                                )
                            ) : (
                                <button
                                    type="button"
                                    className="profile-btn"
                                    onClick={() => handleConnect(key)}
                                    disabled={isConnecting}
                                >
                                    {isConnecting ? 'Connecting...' : 'Connect'}
                                </button>
                            )}
                        </div>
                    </div>
                );
            })}

            <p className="profile-hint" style={{ marginTop: 16 }}>
                Connecting YouTube or Twitch syncs the channels you follow there. Kick does not
                expose a follow list, so Kick alerts cover creators you follow in LiveNow. Synced
                channels appear under Alerts, where you can mute live alerts.
            </p>
        </div>
    );
};

export default AccountConnections;









